import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reads a one-shot success message passed via `navigate(path, { state: { toast: '...' } })`
 * (e.g. "Suche angelegt" / "Änderungen gespeichert" from SubscriptionForm). The message is
 * captured into local state so it stays visible until dismissed, while the `toast` key is
 * immediately stripped from the history entry's state (via a `replace` navigation) so a
 * reload or browser back does not resurface it. Other keys already on `location.state`
 * (e.g. `noRedirect`) are preserved.
 */
export function useLocationToast(): { message: string | null; dismiss: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { toast?: string } | null | undefined;

  const [message, setMessage] = useState<string | null>(state?.toast ?? null);
  const hasClearedRef = useRef(false);

  useEffect(() => {
    if (hasClearedRef.current) return;
    if (!state?.toast) return;

    hasClearedRef.current = true;
    const { toast, ...rest } = state;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(rest).length > 0 ? rest : null,
    });
    // Only ever run once, right after mount with a toast present.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { message, dismiss: () => setMessage(null) };
}
